import { memo, useCallback, useMemo, useState } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import { generatePath, useHistory } from 'react-router-dom'

import { MobileSortBy, Table } from 'components/Table'
import { TableHeader } from 'components/Table/types'
import ROUTES from 'app/routes'

import {
  HomeDevicesComponentCell,
  HomeDevicesRowStyles,
  HomeDevicesTableInnerWrapper,
  HomeDevicesTableOuterWrapper,
} from './styles'
import type { Devices } from '../types'
import { MobileDevicesComponent } from './MobileDevicesComponent'
import StatusIndicator from '../StatusIndicator'
import { useHomeRouteParams } from 'features/home/hooks'
import { useAppSelector } from 'app/hooks'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import { getSortOptions, sortByInsensitiveString } from 'components/Table/utils'

import { sortByStatus, shouldShowDeviceIP } from '../utils'

interface HomeDevicesDesktopTableProps {
  headers: Record<string, TableHeader>
  devices: Devices[]
  searchValue?: string
}

function HomeDevicesTable({
  headers,
  devices,
  searchValue,
}: HomeDevicesDesktopTableProps) {
  const history = useHistory()
  const { homeId } = useHomeRouteParams()
  const currentRootId = useAppSelector(currentRootDomainIdSelector)

  const NameCell = useCallback(
    (cell: Cell) => {
      const handleClick = (deviceName: string) => () => {
        if (currentRootId) {
          const device: Devices = cell.row.original as any
          const devicePath = generatePath(ROUTES.homeDevice, {
            // intentionally falsey and not nullish
            id: device?.statusPath || device?.id || device?.deviceName,
            homeId,
            rootDomainId: currentRootId,
          })
          return history.push(devicePath)
        }
      }
      return (
        <HomeDevicesComponentCell onClick={handleClick(cell.value)}>
          {cell.value}
        </HomeDevicesComponentCell>
      )
    },
    [currentRootId, history, homeId]
  )

  const columns: readonly Column<Devices>[] = useMemo(
    () => [
      {
        Header: headers.status.label,
        accessor: 'status',
        Cell: (cell: Cell) => <StatusIndicator status={cell.value} />,
        percentWidth: '10%',
        sortType: sortByStatus,
      },
      {
        Header: headers.deviceName.label,
        accessor: 'deviceName',
        Cell: NameCell,
        percentWidth: '20%',
        sortType: sortByInsensitiveString,
      },
      {
        Header: headers.deviceType.label,
        accessor: 'deviceType',
        percentWidth: '14%',
      },
      {
        Header: headers.manufacturer.label,
        id: 'manufacturer',
        accessor: (values) => values.deviceManufacturer || '-',
        percentWidth: '14%',
      },
      {
        Header: headers.controller.label,
        accessor: 'controller',
        percentWidth: '14%',
      },
      {
        Header: headers.room.label,
        accessor: 'room',
        percentWidth: '14%',
      },
      {
        Header: headers.ipAddress.label,
        id: 'ipAddress',
        accessor: (values) =>
          shouldShowDeviceIP(values.ipAddress) ? values.ipAddress : '-',
        percentWidth: '14%',
      },
    ],
    [NameCell, headers]
  )

  const sortOptions = useMemo(() => getSortOptions(headers, false), [headers])
  const defaultSortBy = sortOptions[1]
  const [sortBy, setSortBy] = useState<SortingRule<Devices>>(defaultSortBy)

  return (
    <HomeDevicesTableOuterWrapper>
      <MobileSortBy<Devices>
        headers={headers}
        sortOptions={sortOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
      <HomeDevicesTableInnerWrapper>
        <Table
          columns={columns}
          data={devices}
          searchValue={searchValue}
          rowStyles={HomeDevicesRowStyles}
          defaultSortBy={defaultSortBy}
          sortBy={sortBy}
          setSortBy={setSortBy}
          isPaginated={true}
          mobileViewComponent={MobileDevicesComponent}
        />
      </HomeDevicesTableInnerWrapper>
    </HomeDevicesTableOuterWrapper>
  )
}

export default memo(HomeDevicesTable)
